/*
| Soft (SPA-like) navigation for internal GET links. Intercepts same-origin nav
| clicks, fetches the target, swaps the #screen region, re-runs the page inits and
| updates history/title — wrapped in a View Transition. Topbar + advisor live
| outside #screen and persist. Progressive: any failure (or missing support) falls
| back to a full navigation, and forms/POST always do a normal request.
*/
import { resetScreenScope } from './screenScope';

const SCREEN_ID = 'screen';

// Same-origin GET nav only; skip the API, auth, downloads, exports and external/new-tab.
const internalGet = (a: HTMLAnchorElement | null): string | null => {
    if (!a || a.target === '_blank' || a.hasAttribute('download') || !a.getAttribute('href') || a.dataset.noSoftnav !== undefined) {
        return null;
    }
    let url: URL;
    try {
        url = new URL(a.href, location.href);
    } catch {
        return null;
    }
    if (url.origin !== location.origin) {
        return null;
    }
    if (/\/(api|logout|auth|export)(\/|$)/.test(url.pathname) || /\.(xlsx|csv|pdf|ics)$/i.test(url.pathname)) {
        return null;
    }
    if (url.href === location.href) {
        return null;
    }
    return url.href;
};

// Inline <script> inserted via DOM swap don't execute — re-create them so page-local
// scripts (e.g. the vacations table sort) run. Skip JSON data islands + external/module.
const runScripts = (container: HTMLElement): void => {
    container.querySelectorAll('script').forEach((old) => {
        const type = old.getAttribute('type') ?? '';
        if (old.src || type === 'application/json' || type === 'module') {
            return;
        }
        const s = document.createElement('script');
        s.textContent = old.textContent;
        old.replaceWith(s);
    });
};

const updateActiveNav = (path: string): void => {
    document.querySelectorAll<HTMLAnchorElement>('.topbar .tab, .nav-overlay .ov-link').forEach((a) => {
        let match = false;
        try {
            match = new URL(a.href).pathname === path;
        } catch {
            match = false;
        }
        if (match) {
            a.setAttribute('aria-current', 'page');
        } else {
            a.removeAttribute('aria-current');
        }
    });
};

export function initSoftNav(reinitScreen: () => void): void {
    if (!window.fetch || !document.getElementById(SCREEN_ID)) {
        return; // no support / nothing to swap → leave normal navigation
    }
    history.scrollRestoration = 'manual';
    history.replaceState({ softnav: true }, '');

    let busy = false;

    const swap = (doc: Document, url: string): void => {
        // Drop popovers/tooltips the previous screen relocated to <body>.
        document.querySelectorAll('body > [data-wp-menu-pop], body > .th-tip, body > .th-menu, body > .th-toast').forEach((el) => el.remove());
        const incoming = doc.getElementById(SCREEN_ID);
        const existing = document.getElementById(SCREEN_ID);
        if (!incoming || !existing) {
            location.href = url;
            return;
        }
        const node = document.importNode(incoming, true);
        resetScreenScope(); // tear down the previous screen's document/window listeners
        existing.replaceWith(node);
        document.title = doc.title;
        runScripts(node as HTMLElement);
        updateActiveNav(new URL(url).pathname);
        reinitScreen();
        document.dispatchEvent(new CustomEvent('planning:navigated'));
    };

    const go = async (url: string, push: boolean): Promise<void> => {
        if (busy) {
            return;
        }
        busy = true;
        try {
            const res = await fetch(url, { headers: { 'X-Softnav': '1' }, credentials: 'same-origin' });
            const ct = res.headers.get('content-type') ?? '';
            if (!res.ok || res.redirected || !ct.includes('text/html')) {
                location.href = res.url || url; // login redirect, error, non-HTML → full nav
                return;
            }
            const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
            if (!doc.getElementById(SCREEN_ID)) {
                location.href = url;
                return;
            }
            if (push) {
                history.pushState({ softnav: true }, '', url);
            }
            // Swap directly — never gate the swap behind the View Transition API, whose
            // update callback can be skipped (e.g. backgrounded/headless), leaving the URL
            // changed but the content stale. The incoming .screen carries .screen-anim-enter,
            // so the CSS enter animation plays on insertion (honours reduced-motion via the
            // central kill switch). Cross-fade between screens is a nice-to-have, not load-bearing.
            swap(doc, url);
            if (push) {
                window.scrollTo(0, 0);
            }
        } catch {
            location.href = url;
        } finally {
            busy = false;
        }
    };

    document.addEventListener('click', (e) => {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
            return;
        }
        const href = internalGet((e.target as HTMLElement)?.closest?.('a'));
        if (!href) {
            return;
        }
        e.preventDefault();
        void go(href, true);
    });

    window.addEventListener('popstate', () => {
        void go(location.href, false);
    });
}
